import {REG} from "/@lib@/commons/registry.js";

/**
 * Stylage racine d'un éditeur.
 * Permet de définir les styles transversaux à tous les binds.
 * A placer sur l'elt racine d'un du bind document et des binds racines des éditeurs en panels.
 * Les <wed:children/> de ce bind root doivent être placés dans un <box-host> pour bénéficier de stylage transversal.
 * Rappel : ces styles ne s'appliquent qu'aux balises racines des binds (pas celles qui entrent dans leurs shadowTree).
 */
REG.reg.registerSkin('box/root', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	.v {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	.v > box-ptritem {
		align-self: stretch;
		margin-left: initial;
		margin-right: initial;
	}

	.h {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: row;
		align-items: center;
	}

	[edit-mode='h'] {
		display: none;
	}

	.v.virtual[edit-mode^='r'],
	.h.virtual[edit-mode^='r'] {
		display: none;
	}

	[selected]:not([selectin]) {
		background-color: var(--edit-select-bgcolor);
	}

	::selection {
		color: currentColor;
		background-color: var(--edit-seltext-bgcolor);
	}


	[annot=error] {
		background-color: var(--edit-forbidden-bgcolor);
	}

	[annot-diff=diffadd] {
		padding-inline-end: 10px;
		background-color: var(--edit-diffadd-bgcolor);
		--edit-bgcolor: none;
	}

	[annot-diff=diffrem] {
		padding-inline-end: 10px;
		background-color: var(--edit-diffrem-bgcolor);
		--edit-bgcolor: none;
	}

	[annot-diff=diff] {
		margin-top: 2px;
		border-inline-end: 1px solid var(--edit-diff-color);
	}

	.forbidden {
		border: 1px dashed #8c0000;
		background-color: var(--edit-forbidden-bgcolor, rgba(239, 0, 0, 0.1));
	}

	/* obligatoire pour les box sans shadowRoot. */
	box-ctn:focus,
	box-ctn-input:focus {
		outline: var(--edit-box-focus);
	}

	.commentXml {
		padding-inline-start: 1em;
	}
`)
/*::inactive-selection {*/
/*  background-color: var(--edit-select-unfocus-bgcolor);*/
/*}*/

/*[selected]:not([selectin]):not(:focus) {*/
/*  background-color: var(--edit-select-unfocus-bgcolor);*/
/*}*/

/*[annot-search] {*/
/*  outline: 3px solid var(--edit-search-bgcolor); non, écrase l'outline du focus*/
/*}*/


/**
 * Root dans le contexte d'un item.
 * Surcharge "box/root".
 */
REG.reg.registerSkin('box/root/item', 1, /* language=CSS */ `
	:host {
		padding-bottom: 5vh;
	}
`);

/**
 * Root dans le contexte d'un fragment (wedDiffRem, boxScExclude...)
 * Surcharge "box/root".
 */
REG.reg.registerSkin('box/root/fragment', 1, /* language=CSS */ `
	/*:host {*/
	/*	margin: .4rem;*/
	/*}*/
`);

/**
 * Root dans le contexte d'un panel (édition de meta).
 * Surcharge "box/root".
 */
REG.reg.registerSkin('box/root/panel', 1, /* language=CSS */ `
	:host {
		margin: .4rem;
	}
`);

/**
 * Style pour les éléments génériques.
 */
REG.reg.registerSkin('box/genElt', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		padding: 1px;
	}

	:host(.forbidden) {
		background-color: #f8e2e2;
	}

	.head {
		flex-flow: row wrap;
		display: flex;
		min-height: 0;
		min-width: 0;
		align-items: center;
		margin-inline-end: .5em; /*pour box-insert*/
	}

	box-nameinput {
		font-weight: bold;
		background-color: var(--edit-bgcolor);
		color: var(--alt1-color, blueviolet);
		padding: .1em;
		border-radius: .2em;
	}

	box-nameinput:empty {
		padding-inline-start: .3em;
		padding-inline-end: .3em;
		background-color: #ffece3;
	}

	box-nameinput.forbidden {
		color: red;
	}

	.body {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		padding-inline-start: .4rem;
		border-inline-start: 1px solid transparent;
	}

	.body:hover {
		border-inline-start: 1px dotted var(--border-color);
	}

	box-insert {
		user-select: none;
		-webkit-user-modify: read-only;
		display: none;
		z-index: 1000;
		position: relative;
	}

	:host([selected]) box-insert:not([edit-mode]) {
		display: block;
	}

	box-insert > button {
		padding: 0 2px;
		margin: 0;
		border: none;
		background: var(--insbtn-bg);
		color: var(--insbtn-color);
		opacity: .8;
		position: absolute;
	}

	box-insert > button:before {
		content: "+";
	}

	box-insert[select='@'] > button {
		margin-inline-start: .2em;
		margin-top: -.5em;
	}

	box-insert[select='* #'] > button {
		margin-top: -.2em;
	}

	box-insert > button:hover {
		background: var(--insbtn-hover-bg);
	}

	box-insert > button:focus,
	box-nameinput:focus {
		outline: var(--edit-box-focus);
	}
`);


/**
 * Style pour les attributs génériques.
 */
REG.reg.registerSkin('box/genAtt', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: row;
		align-items: center;
		margin-inline-start: 0.5em;
		padding: 0 .2em;
	}

	:host(.forbidden) {
		background-color: #f8e2e2;
	}

	box-nameinput {
		background-color: var(--edit-bgcolor);
		color: var(--alt2-color, brown);
		padding: .1em;
		border-radius: .2em;
	}

	box-nameinput:empty {
		padding-inline-start: .3em;
		padding-inline-end: .3em;
		background-color: #ffece3;
	}

	box-nameinput.forbidden {
		color: var(--error-color);
	}

	box-input-ctedit {
		border: none;
		padding: 1px;
		min-width: .8em;
	}

	box-input-ctedit:focus-within {
		background-color: var(--edit-bgcolor);
	}

	:host(:focus),
	box-nameinput:focus {
		outline: var(--edit-box-focus);
	}

	.quote {
		user-select: none;
		color: var(--fade-color);
	}
`);

/**
 * Style pour les texte génériques.
 */
REG.reg.registerSkin('box/genTxt', 1, /* language=CSS */ `
	:host {
		display: block;
		padding: .2em;
		border-inline-start-width: .5em;
	}

	:host([selected]) > box-input-ctedit {
		background-color: var(--edit-bgcolor);
	}

	box-input-ctedit {
		border-inline-start-width: .5em;
	}
`);

/**
 * Style pour les commentaires génériques.
 */
REG.reg.registerSkin('box/comment', 1, /* language=CSS */ `
	:host {
		display: block;
		background-color: rgba(207, 255, 96, .2);
		margin: .5em;
		padding: .2em;
		padding-inline-start: 1em;
	}

	:host(:focus) {
		outline: var(--edit-box-focus);
	}

	:host([selected]) > box-input-ctedit {
		background-color: var(--edit-bgcolor);
	}
`);

/**
 * Stylage des attributs placés dans la zone de titre d'un tag
 */
REG.reg.registerSkin('box/head-attribute', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: row;
		margin-inline-end: 0.8rem;
		align-items: center;
	}

	:host(.forbidden) {
		background-color: #f8e2e2;
	}

	.label {
		color: rgba(98, 119, 105, 0.71);
		padding-block: 2px;
		padding-inline: 0 2px;
	}

	.label::after {
		content: " : ";
	}

	.label.virtual {
		filter: var(--fade-filter);
	}

	.value {
		min-width: 5em;
	}

	:host(:focus) {
		outline: var(--edit-box-focus);
	}
`);


/**
 * Stylage des widgets structurés avec un head et un body.
 * Utilisé pour les blocs mais aussi les fields (inputs dans le wed:children de widget, et txt-root-inline dans ce widget)
 */
REG.reg.registerSkin('box/head-body', 1, /* language=CSS */ `
	.label {
		min-width: 8rem;
		padding-block: 2px;
		padding-inline: 0 2px;
		user-select: none;
	}

	.head {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: row;
		align-items: center;
		color: var(--alt1-color);
	}

	:host([edit-mode=rs]) .head {
		display: none;
	}

	:host(.virtual) > .head {
		filter: var(--fade-filter);
	}

	:host(.h) > .body {
		padding-inline-start: 0;
		margin-inline-start: 0;
	}

	:host(:focus) {
		outline: var(--edit-box-focus);
	}

	/* Sub-level */
	:host > .sublevel {
		border-inline-start: 1px dotted transparent;
	}

	:host > .sublevel:hover {
		border-inline-start-color: var(--alt1-border-color);
	}

	box-btnreplace-in {
		position: absolute;
		top: 2em;
		left: 0;
	}
`);

/** - layout "block" **/
REG.reg.registerSkin('box/head-body/block', 1, /* language=CSS */ `
	:host {
		display: block;
		padding: 1px;
	}

	.body {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex: 1;
		flex-direction: column;
		padding-inline-start: .4rem;
		margin-inline-start: .4rem;
		border-inline-start: 1px solid transparent;
	}

	:host(.h) > .body {
		padding-inline-start: 0;
		margin-inline-start: 0;
	}

	:host(:focus) {
		outline: var(--edit-box-focus);
	}
`);

/** - layout "float" **/
REG.reg.registerSkin('box/head-body/float', 1, /* language=CSS */ `
	:host {
		display: block;
		padding-inline-start: .8rem;
	}

	.head {
		float: start;
		margin-inline-start: -.8rem;
	}

	.body {
		display: contents;
	}

	:host(:focus) {
		outline: var(--edit-box-focus);
	}
`);

/**
 * Stylage des binds de l'éditeur de texte riche.
 *
 * TODOsc7 passer txt-para {padding-top: .3em; padding-bottom: .3em} pour éviter une surcharge systématique dans le modèle.
 */
REG.reg.registerSkin('box/richtext', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 1rem;
		flex-direction: column;
	}

	/*mono-line*/
	:host(.mnLn) {
		border-bottom: 1px solid var(--border-color);
	}

	/*multi-line*/
	:host(.mtLn) {
		border: 1px solid var(--border-color);
	}

	:host([edit-mode=rs]) {
		border: none;
	}

	txt-root {
		user-select: text;
	}

	txt-para {
		padding-top: 2px;
		padding-bottom: 2px;
	}

	txt-para.virtual:not(:only-of-type):not(.regular) {
		font-size: .3em;
		margin: 0;
		padding: 0;
		line-height: 0;
	}

	inl-object {
		background-color: #879e8a40;
	}

	inl-img, inl-object, txt-object {
		position: relative;
	}

	.editLnk {
		display: none;
		position: absolute;
		right: -.4em;
		top: -.4em;
		width: 1.2em;
		height: 1.2em;
		cursor: pointer;
		user-select: none;
		background: no-repeat top / contain url(/@skin@/wsp/actions/fastFind.svg) #FFFFFFAA;
		border: 1px solid var(--border-color);
		border-radius: .2em;
	}

	:focus-visible > .editLnk {
		display: block;
	}

	txt-row {
		vertical-align: middle;
	}

  .forbidden {
		background-color: rgba(239, 0, 0, 0.1);
	}

	.unknown {
		padding: 0 .5em;
		font-family: var(--font-mono);
		color: var(--error-color);
	}

	.diffInMeta {
		outline: 2px solid var(--edit-diff-color);
	}

	x-nbsp::before {
		content: '\\A0';
		background-color: var(--edit-nbsp-bgcolor, rgba(150, 150, 150, 0.5));
	}

	mark {
		color: var(--edit-color);
	}

	mark.diffadd {
		background-color: var(--edit-diffadd-bgcolor);
		outline: 1px solid var(--edit-diffadd-color);
	}

	mark.diffrem {
		background-color: var(--edit-diffrem-bgcolor);
		outline: 1px solid var(--edit-diffrem-color);
		text-decoration: line-through solid var(--edit-diffrem-color);
	}

	[diff-para] {
		border-inline-start: 1px solid var(--edit-diff-bgcolor);
		padding-inline-end: 10px;
		position: relative;
	}

	[diff-para] > box-editor-frag {
		background-color: var(--edit-diff-bgcolor);
		--edit-bgcolor: none;
		display: block;
	}

	txt-table.diff {
		border: 4px solid var(--edit-diff-bgcolor);
		margin-top: 0 !important;
	}

	mark.search {
		background-color: var(--edit-search-bgcolor);
		color: unset;
	}

	:not(:focus) > txt-para[placeholder].virtual:only-of-type::before,
  :not(:focus) > txt-caption[placeholder].virtual:only-of-type::before,
  :not(:focus) > txt-title[placeholder].virtual:only-of-type::before,
	[mono-para]:not(:focus) > txt-para > txt-str[placeholder].virtual:only-of-type::before {
		content: attr(placeholder);
		color: var(--fade-color);
		filter: var(--fade-filter);
		letter-spacing: .2em;
		font-style: italic;
		cursor: text;
		padding-inline-start: .3em;
	}

	txt-root[dragOn=sel] ::selection {
		background-color: #1a0498;
		color: white;
	}

	[annot=error] {
		background-color: var(--edit-forbidden-bgcolor, rgba(239, 0, 0, 0.1));
	}

	[annot-diff] {
		padding-inline-end: 10px;
	}

	[annot-diff=diffadd] {
		background-color: var(--edit-diffadd-bgcolor);
		--edit-bgcolor: none;
	}

	[annot-diff=diffrem] {
		background-color: var(--edit-diffrem-bgcolor);
		--edit-bgcolor: none;
	}

	:focus-visible {
		outline: var(--edit-caret-focus);
	}

	::selection {
		color: currentColor;
		background-color: var(--edit-seltext-bgcolor);
	}

	/*::inactive-selection {*/
	/*  background-color: var(--edit-select-unfocus-bgcolor);*/
	/*}*/

	txt-empty[in-sel], txt-object[in-sel] {
		outline: 2px dotted var(--edit-seltext-bgcolor);
		outline-offset: -2px;
	}

	inl-object[in-sel], inl-empty[in-sel], inl-img[in-sel], inl-note[in-sel] {
		outline: 1px dotted var(--edit-seltext-bgcolor);
		outline-offset: -1px;
	}

	.editingMeta {
		outline: 2px solid var(--edit-seltext-bgcolor);
	}
`);


REG.reg.registerSkin('box/str', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-width: min-content;
		min-height: min-content;
		flex-direction: column;
		border-bottom: 1px solid var(--border-color);
	}

	:host([edit-mode=rs]) {
		border: none;
	}

	x-nbsp::before {
		content: '\\A0';
		background-color: var(--edit-nbsp-bgcolor, rgba(150, 150, 150, 0.5));
	}

	:not(:focus) > txt-str-alone[placeholder].virtual::before {
		content: attr(placeholder);
		color: var(--fade-color);
		filter: var(--fade-filter);
		font-style: italic;
		letter-spacing: 2px;
	}

	:focus-visible {
		outline: var(--edit-caret-focus);
	}

	::selection {
		color: currentColor;
		background-color: var(--edit-seltext-bgcolor);
	}

	[annot-diff=diffadd] {
		padding-inline-end: 10px;
		background-color: var(--edit-diffadd-bgcolor);
		--edit-bgcolor: none;
	}

	/*::inactive-selection {*/
	/*  background-color: var(--edit-select-unfocus-bgcolor);*/
	/*}*/
`);

REG.reg.registerSkin('box/richtext/fragment', 1, /* language=CSS */ `
	:host,
	txt-root {
		margin: 0 !important;
		padding: 0 !important;
		border: none !important;
		border-radius: 0 !important;
	}

	.fragRoot {
		border: none;
		margin: 0;
	}

	[if-in-fragment=hide] {
		display: none;
	}
`);

REG.reg.registerSkin('box-collaps/collapsed', 1, /* language=CSS */ `
	:host {
		display: flex !important;
		flex-direction: row !important;
	}

	.head {
		flex-direction: row !important;
	}

	.label {
		flex: 1;
	}

	.body {
		flex: 1;
		display: block !important;
		filter: grayscale(100%) opacity(70%) !important;
		min-height: 0 !important;
		max-height: 2em !important;
		overflow: hidden !important;
		overflow-anchor: none !important;
	}

	:host([skin='box/genElt']) > .body {
		display: none !important;
	}

	:host([skin='box/genElt']) > .head {
		flex: 1 !important;
	}

	#decollapser {
		min-width: 1em;
		min-height: 1em;
		background: no-repeat top / contain url(/@skin@/edit/wed/box/uncollapse.svg);
		cursor: pointer;
	}

`);

REG.reg.registerSkin('box-collaps/uncollapsed', 1, /* language=CSS */ `
	#collapser {
		min-width: 1em;
		min-height: 1em;
		background: no-repeat top / contain url(/@skin@/edit/wed/box/collapse.svg);
		cursor: pointer;
	}
`);

REG.reg.registerSkin('box/id', 1, /* language=CSS */ `
	:host(.virtual) {
		filter: var(--fade-filter);
		pointer-events: none;
	}
`);

REG.reg.registerSkin('box-collaps/collapsed_under', 1, /* language=CSS */ `
	:host {
		display: grid !important;
		grid-template-columns: auto 1fr;
		grid-template-rows: auto auto;
	}

	.head {
		grid-column: 1;
		grid-row: 1;
	}

	.label {
		flex: 1;
	}

	.body {
		grid-column: 2;
		grid-row: 1;
		display: block !important;
		filter: grayscale(100%) opacity(70%) !important;
		min-height: 0 !important;
		max-height: 2em !important;
		overflow: hidden !important;
		overflow-anchor: none !important;
	}

	#decollapser {
		grid-column: 1 / 3;
		grid-row: 2;
		min-height: .5rem;
		border-image: url(/@skin@/modeling/edit/wed/box/uncollapsUnderBorder.svg) 100% 0 0 0;
		border-top: 3px solid;
		background: no-repeat top / contain url(/@skin@/modeling/edit/wed/box/uncollapsUnder.svg);
		cursor: pointer;
	}
`);


REG.reg.registerSkin('box/input-url/preview', 1, /* language=CSS */ `
	:host {
		display: grid;
		grid-template-columns: max-content 1fr max-content;
		align-items: center;
	}

	.control {
		margin-inline-end: .5em;
		grid-column: 1;
		grid-row: 1;
	}

	.input {
		grid-column: 2;
		grid-row: 1;
	}

	.buttons {
		margin-inline-start: .5em;
		grid-column: 3;
		grid-row: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: row;
	}


	:host(.virtual) .preview {
		display: none;
	}

	.preview {
		grid-column: 1 / 4;
		grid-row: 2;
	}
`);

REG.reg.registerSkin('box/input-radio', 1, /* language=CSS */ `
	:host(.v) .actionsList {
		flex-direction: column;
	}
`);